import { QueryObserverResult, useQuery } from "@tanstack/react-query";
import { Chain, CHAIN_CONFIG } from "chainConfigs";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import axiosService from "services/axios";
import useTradeStore from "store/trade-store.ts/useTradeStore";
import { WalletAddresses } from "store/user-store-v2";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { Mode, TRADING_MODE_CONFIG_MAP } from "tradeConfigs";

const CHAIN_WALLET_MAP: Record<string, keyof WalletAddresses> = {
  solana: "solAddress",
  ethereum: "ethAddress",
  base: "ethAddress",
  berachain: "ethAddress",
  bsc: "ethAddress",
};

export interface ITradeContext {
  defaultMode: Mode;
  mode: Mode;
  selectedNativeChain: Chain;
  jettonBuyRatio: number | null;
  jettonSellRatio: number | null;
  nativeTokenPrice: number | null;
  nativeBalanceUsd: number | null;
  nativeBalanceAmount: number | null;
  isNativeTokenPriceLoading: boolean;
  isNativeTokenPriceRefetching: boolean;
  selectedTargetChain: Chain;
  targetTokenConfig: Record<string, TokenConfig>;
  targetTokenPrice: number | null;
  targetBalanceUsd: number | null;
  targetBalanceAmount: number | null;
  targetBalancePerformance: number | null;
  targetBalanceProfit: number | null;
  isTargetTokenBalanceLoading: boolean;
  isTargetTokenBalanceRefetching: boolean;
  nativeToTargetTokenRatio: number | null;
  senderAddress: string;
  isDepositModalOpen: boolean;
  isTrading: boolean;
  tgGroupId?: number | null;
  destinationAddress?: string | null;
  getNativePriceByChain: (chain: Chain) => number | null;
  refetchPriceRatio: () => void;
  refetchTargetTokenBalance: () => Promise<QueryObserverResult<any, Error>>;
  handleSetMode: (mode: Mode) => Promise<void>;
  handleSetIsTrading: (isTrading: boolean) => void;
  handleSetSelectedNativeChain: (chain: Chain) => void;
  handleSetSelectedTargetChain: (chain: Chain) => void;
  isPolling: boolean;
  setIsPolling: (isPolling: boolean) => void;
  isTonBoardcasting: boolean;
  setIsTonBoardcasting: (isTonBoardcasting: boolean) => void;
  txnHash: string | null;
  setTxnHash: (txnHash: string | null) => void;
  isTradeInitingRef: React.MutableRefObject<boolean>;
  handleResetToDefaultTargetChain: () => void;
  updateUserBalance: () => void;
}

export const TradeContext = createContext<ITradeContext>({
  defaultMode: "BUY",
  mode: "BUY",
  selectedNativeChain: "base",
  jettonBuyRatio: null,
  jettonSellRatio: null,
  nativeTokenPrice: null,
  nativeBalanceUsd: null,
  nativeBalanceAmount: null,
  isNativeTokenPriceLoading: false,
  isNativeTokenPriceRefetching: false,
  selectedTargetChain: "ethereum",
  targetTokenConfig: {},
  targetTokenPrice: null,
  targetBalanceUsd: null,
  targetBalanceAmount: null,
  targetBalancePerformance: null,
  targetBalanceProfit: null,
  isTargetTokenBalanceLoading: false,
  isTargetTokenBalanceRefetching: false,
  nativeToTargetTokenRatio: null,
  senderAddress: "",
  isDepositModalOpen: false,
  isTrading: false,
  tgGroupId: null,
  destinationAddress: null,
  getNativePriceByChain: () => null,
  refetchPriceRatio: () => {},
  refetchTargetTokenBalance: () => Promise.resolve({} as QueryObserverResult<any, Error>),
  handleSetMode: () => Promise.resolve(),
  handleSetIsTrading: () => {},
  handleSetSelectedNativeChain: () => {},
  handleSetSelectedTargetChain: () => {},
  isPolling: false,
  setIsPolling: () => {},
  isTonBoardcasting: false,
  setIsTonBoardcasting: () => {},
  txnHash: null,
  setTxnHash: () => {},
  isTradeInitingRef: { current: false },
  handleResetToDefaultTargetChain: () => {},
  updateUserBalance: () => {},
});

export interface ITradeConfig {
  destToken: string;
  srcToken: string;
  destChains: string[];
  nativeChains: string[];
}

export interface TokenConfig {
  chain: string;
  symbol?: string;
  token?: string;
  imageUrl?: string;
}

export interface ITradeProps {
  chain: Chain;
  mode?: Mode;
  targetTokenConfig: Record<string, TokenConfig>;
  children: React.ReactNode;
}

export function TradeProvider(props: ITradeProps) {
  // Data
  const defaultMode = props.mode ?? "BUY";
  const [mode, setMode] = useState<Mode>(props.mode ?? "BUY");
  const [isPolling, setIsPolling] = useState(false);
  const [txnHash, setTxnHash] = useState<string | null>(null);
  const [isTonBoardcasting, setIsTonBoardcasting] = useState(false);
  const { chain, targetTokenConfig } = props;

  // Chains List
  // const nativeChains = Object.keys(nativeTokenConfig);
  // const targetChains = Object.keys(targetTokenConfig);

  // Current chain
  const [selectedNativeChain, setSelectedNativeChain] = useState<Chain>(chain);
  const [selectedTargetChain, setSelectedTargetChain] = useState<Chain>(
    TRADING_MODE_CONFIG_MAP[mode].to.isBlockedSameChain
      ? TRADING_MODE_CONFIG_MAP[mode].supportedNativeChains[chain]!.find((chainOption) => {
          return chainOption !== selectedNativeChain;
        })!
      : chain,
  );

  // trade initing
  const isTradeInitingRef = useRef(false);

  // User
  const { walletAddresses } = useUserStoreV2();

  // Flags
  const [isTrading, setIsTrading] = useState(false);

  // Deposit Modal
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);

  const fetchBalanceTypeRef = useRef<"realTime" | "archive">("archive");

  // Balance
  const {
    isLoading: isLoadingUserBalance,
    updateUserBalance,
    nativeTokenAmountBalance,
    nativeTokenUsdBalance,
  } = useTradeStore();

  // Sender Address
  const senderAddress =
    walletAddresses?.[
      CHAIN_WALLET_MAP[
        (mode === "BUY" || mode === "SWAP" ? selectedNativeChain : selectedTargetChain) as Chain
      ]
    ] ?? "";

  useEffect(() => {
    if (!isLoadingUserBalance && nativeTokenAmountBalance[chain] !== null) {
      const defaultChainNativeAmount = nativeTokenAmountBalance[chain];

      if (defaultChainNativeAmount! <= 0) {
        // TODO: Revert for berachain
        const balanceList = ["ethereum", "solana", "base", "berachain", "bsc"].map((chain) => {
          return {
            chain,
            amount: nativeTokenAmountBalance[chain as Chain],
          };
        });

        if (balanceList.every((balance) => balance.amount === null || balance.amount <= 0)) {
          setIsDepositModalOpen(true);
        } else {
          const largestBalance = balanceList.sort((a, b) => b.amount! - a.amount!)[0];
          setSelectedNativeChain(largestBalance.chain as Chain);
        }
      }
    }
  }, [chain, isLoadingUserBalance, nativeTokenAmountBalance]);

  // Get Token Price Ratio
  const {
    data: priceRatioData,
    isLoading: isNativeTokenPriceLoading,
    refetch: refetchPriceRatio,
    isRefetching: isNativeTokenPriceRefetching,
  } = useQuery({
    queryKey: ["target-token-price-ratio", selectedTargetChain],
    queryFn: () => {
      return axiosService.getNativePrice({
        tokenAddress: targetTokenConfig[selectedTargetChain]?.token!,
        chain: selectedTargetChain,
      });
    },
    enabled: !!targetTokenConfig[selectedTargetChain]?.token,
  });

  // Get Token Balance
  const {
    data: targetTokenBalanceData,
    isLoading: isTargetTokenBalanceLoading,
    refetch: refetchTargetTokenBalance,
    isRefetching: isTargetTokenBalanceRefetching,
  } = useQuery({
    queryKey: ["target-token-balance", targetTokenConfig[selectedTargetChain]?.token],
    queryFn: () => {
      if (!senderAddress) {
        return;
      }

      return axiosService.getTokenBalance({
        tokenAddress: targetTokenConfig[selectedTargetChain]?.token!,
        walletAddress: senderAddress,
        chain: selectedTargetChain,
        type: fetchBalanceTypeRef.current,
      });
    },
    enabled: !!targetTokenConfig[selectedTargetChain]?.token,
  });

  const handleSetSelectedNativeChain = (chain: Chain) => {
    setSelectedNativeChain(chain);
    if (TRADING_MODE_CONFIG_MAP[mode].to.isBlockedSameChain) {
      setSelectedTargetChain(
        TRADING_MODE_CONFIG_MAP[mode].supportedNativeChains[chain]!.find(
          (chainOption) => chainOption !== chain,
        )!,
      );
    }
  };

  const handleSetSelectedTargetChain = (chain: Chain) => {
    setSelectedTargetChain(chain);
  };

  const handleSetIsTrading = (isTrading: boolean) => {
    setIsTrading(isTrading);
  };

  const handleSetMode = (mode: Mode) => {
    return new Promise<void>((resolve) => {
      setMode(mode);
      setSelectedNativeChain(chain);
      setSelectedTargetChain(chain);
      resolve();
    });
  };

  const getNativePriceByChain = (chain: Chain) => {
    if (priceRatioData) {
      return priceRatioData[CHAIN_CONFIG[chain].nativePriceName];
    } else {
      return null;
    }
  };

  const handleResetToDefaultTargetChain = () => {
    setSelectedTargetChain(
      TRADING_MODE_CONFIG_MAP[mode].to.isBlockedSameChain
        ? TRADING_MODE_CONFIG_MAP[mode].supportedNativeChains[chain]!.find(
            (chainOption) => chainOption !== selectedNativeChain,
          )!
        : chain,
    );
  };

  return (
    <TradeContext.Provider
      value={{
        // Mode
        defaultMode,
        mode,
        // Native Token
        selectedNativeChain,
        jettonBuyRatio: priceRatioData?.jettonBuyRatio,
        jettonSellRatio: priceRatioData?.jettonSellRatio,
        nativeTokenPrice:
          priceRatioData?.[CHAIN_CONFIG[selectedNativeChain].nativePriceName] ?? null,
        nativeBalanceUsd: nativeTokenUsdBalance[selectedNativeChain as Chain] ?? null,
        nativeBalanceAmount: nativeTokenAmountBalance[selectedNativeChain as Chain] ?? null,
        isNativeTokenPriceLoading,
        isNativeTokenPriceRefetching,
        // Target Token
        targetTokenConfig,
        selectedTargetChain,
        targetTokenPrice:
          mode !== "SWAP"
            ? priceRatioData?.price
            : priceRatioData?.[CHAIN_CONFIG[selectedTargetChain].nativePriceName] ?? null,
        targetBalanceUsd: targetTokenBalanceData?.usdValue,
        targetBalanceAmount: targetTokenBalanceData?.quantity,
        targetBalancePerformance: targetTokenBalanceData?.performance,
        targetBalanceProfit: targetTokenBalanceData?.profit,
        isTargetTokenBalanceLoading,
        isTargetTokenBalanceRefetching,
        nativeToTargetTokenRatio:
          getNativePriceByChain(selectedNativeChain) / priceRatioData?.price,
        // Trade
        destinationAddress:
          mode === "BUY" || mode === "SWAP"
            ? walletAddresses?.[CHAIN_WALLET_MAP[selectedTargetChain]]
            : walletAddresses?.[CHAIN_WALLET_MAP[selectedNativeChain]],
        // Wallets
        senderAddress,
        // States
        isTrading,
        isDepositModalOpen,
        getNativePriceByChain,
        refetchPriceRatio,
        refetchTargetTokenBalance,
        handleSetMode,
        handleSetIsTrading,
        handleSetSelectedNativeChain,
        handleSetSelectedTargetChain,
        isPolling,
        setIsPolling,
        isTonBoardcasting,
        setIsTonBoardcasting,
        txnHash,
        setTxnHash,
        isTradeInitingRef,
        handleResetToDefaultTargetChain,
        updateUserBalance,
      }}>
      {props.children}
    </TradeContext.Provider>
  );
}

export const useTradeContext = () => useContext(TradeContext);
